// src/pages/CreateInvoicePage.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, getDocs, orderBy, Timestamp, writeBatch, doc, runTransaction, getDoc, where, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import styles from '../styles/CreateInvoicePage.module.css';
import { FiPlus, FiTrash2 } from 'react-icons/fi';

const currencyILS = (n) => new Intl.NumberFormat('en-IL', { style: 'currency', currency: 'ILS' }).format(n || 0);

const CreateInvoicePage = () => {
    const { currentUser, userRole } = useAuth();
    const navigate = useNavigate();
    const isMounted = useRef(true);

    // Form State
    const [clients, setClients] = useState([]);
    const [usersToNotify, setUsersToNotify] = useState([]);
    const [nextInvoiceNumber, setNextInvoiceNumber] = useState('Loading...');
    const [clientId, setClientId] = useState('');
    const [issueDate, setIssueDate] = useState(new Date().toISOString().split('T')[0]);
    const [dueDate, setDueDate] = useState('');
    const [lineItems, setLineItems] = useState([{ description: '', quantity: 1, unitPrice: 0 }]);
    const [notes, setNotes] = useState('');
    const [taxRate, setTaxRate] = useState(0);

    // Helper State
    const [loading, setLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    // Fetch clients, users for notifications, and the next invoice number
    useEffect(() => {
        const fetchData = async () => {
            if (!isMounted.current) return;
            try {
                // Fetch Clients
                const clientsSnapshot = await getDocs(query(collection(db, 'clients'), orderBy('name')));
                const clientsList = clientsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                if (isMounted.current) setClients(clientsList);

                // Fetch Managers and Sales for notifications
                const usersQuery = query(collection(db, 'users'), where('role', 'in', ['Manager', 'Sales']));
                const usersSnapshot = await getDocs(usersQuery);
                const usersList = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                if (isMounted.current) setUsersToNotify(usersList);

                // Fetch the next invoice number
                const counterRef = doc(db, 'counters', 'invoiceCounter');
                const counterSnap = await getDoc(counterRef);
                let nextNumber = 1;
                if (counterSnap.exists()) nextNumber = counterSnap.data().currentNumber + 1;
                if (isMounted.current) {
                    setNextInvoiceNumber(`INV-${String(nextNumber).padStart(6, '0')}`);
                }
            } catch (err) {
                console.error("Error fetching initial data:", err);
                if (isMounted.current) setError("Failed to load required data. Please refresh the page.");
            } finally {
                if (isMounted.current) setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleLineItemChange = useCallback((index, field, value) => {
        const updatedItems = [...lineItems];
        updatedItems[index][field] = value;
        setLineItems(updatedItems);
    }, [lineItems]);

    const addLineItem = useCallback(() => {
        setLineItems([...lineItems, { description: '', quantity: 1, unitPrice: 0 }]);
    }, [lineItems]);

    const removeLineItem = useCallback((index) => {
        if (lineItems.length > 1) {
            const updatedItems = lineItems.filter((_, i) => i !== index);
            setLineItems(updatedItems);
        }
    }, [lineItems]);

    const totals = useMemo(() => {
        const subtotal = lineItems.reduce((acc, item) => {
            const quantity = parseFloat(item.quantity) || 0;
            const unitPrice = parseFloat(item.unitPrice) || 0;
            return acc + (quantity * unitPrice);
        }, 0);
        const taxAmount = subtotal * (parseFloat(taxRate) / 100 || 0);
        const totalAmount = subtotal + taxAmount;
        return { subtotal, taxAmount, totalAmount };
    }, [lineItems, taxRate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');

        if (!clientId) { setError("Please select a client."); return; }
        if (!issueDate || !dueDate) { setError("Both issue date and due date are required."); return; }
        if (new Date(dueDate) < new Date(issueDate)) { setError("Due date cannot be before the issue date."); return; }
        if (lineItems.some(item => !item.description.trim() || item.quantity <= 0 || item.unitPrice < 0)) {
            setError("All line items must have a description, a positive quantity, and a non-negative price.");
            return;
        }

        setIsProcessing(true);
        const selectedClient = clients.find(c => c.id === clientId);
        
        try {
            await runTransaction(db, async (transaction) => {
                const counterRef = doc(db, 'counters', 'invoiceCounter');
                const counterDoc = await transaction.get(counterRef);

                let newInvoiceCount = 1;
                if (counterDoc.exists()) newInvoiceCount = counterDoc.data().currentNumber + 1;

                const formattedInvoiceNumber = `INV-${String(newInvoiceCount).padStart(6, '0')}`;

                const finalLineItems = lineItems.map(item => ({
                    ...item,
                    total: (parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0)
                }));

                const invoiceData = {
                    invoiceNumber: formattedInvoiceNumber,
                    clientId,
                    clientName: selectedClient?.name || 'Unknown Client',
                    issueDate: Timestamp.fromDate(new Date(issueDate + "T00:00:00")),
                    dueDate: Timestamp.fromDate(new Date(dueDate + "T00:00:00")),
                    lineItems: finalLineItems,
                    subtotal: totals.subtotal,
                    taxRate: parseFloat(taxRate) || 0,
                    taxAmount: totals.taxAmount,
                    totalAmount: totals.totalAmount,
                    notes: notes.trim(),
                    status: 'Pending Approval',
                    createdByUid: currentUser.uid,
                    createdByName: currentUser.displayName || currentUser.email,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp(),
                };

                const newInvoiceRef = doc(collection(db, 'invoices'));
                transaction.set(newInvoiceRef, invoiceData);

                const notificationMessage = `New invoice #${invoiceData.invoiceNumber} for ${invoiceData.clientName} (${currencyILS(invoiceData.totalAmount)}) requires your approval.`;
                usersToNotify.forEach(user => {
                    const notificationRef = doc(collection(db, 'notifications'));
                    transaction.set(notificationRef, {
                        userId: user.id,
                        message: notificationMessage,
                        link: `/invoices/${newInvoiceRef.id}`,
                        timestamp: serverTimestamp(),
                        isRead: false,
                        createdBy: currentUser.uid,
                        createdByName: currentUser.displayName,
                        type: 'invoice_approval_request'
                    });
                });

                transaction.set(counterRef, { currentNumber: newInvoiceCount });
            });

            if (isMounted.current) {
                setSuccessMessage("Invoice created and sent for approval!");
                setTimeout(() => navigate('/invoices'), 2000);
            }

        } catch (err) {
            console.error("Error in transaction:", err);
            if (isMounted.current) setError("Failed to create invoice due to a conflict or error. Please try again.");
        } finally {
            if (isMounted.current) setIsProcessing(false);
        }
    };

    if (loading) {
        return <div className={styles.loadingIndicator}>Loading...</div>;
    }

    return (
        <div className={styles.pageContainer}>
            <h1 className={styles.pageTitle}>Create New Invoice</h1>

            <form onSubmit={handleSubmit} className={styles.invoiceForm}>
                <div className={styles.topSection}>
                    <div className={styles.formGroup}>
                        <label htmlFor="client">Client *</label>
                        <select id="client" value={clientId} onChange={(e) => setClientId(e.target.value)} required disabled={isProcessing}>
                            <option value="">-- Select a Client --</option>
                            {clients.map(client => (
                                <option key={client.id} value={client.id}>{client.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="invoiceNumber">Invoice #</label>
                        <input type="text" id="invoiceNumber" value={nextInvoiceNumber} readOnly disabled className={styles.readOnlyInput} />
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="issueDate">Issue Date *</label>
                        <input type="date" id="issueDate" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} required disabled={isProcessing} />
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="dueDate">Due Date *</label>
                        <input type="date" id="dueDate" value={dueDate} onChange={(e) => setDueDate(e.target.value)} required disabled={isProcessing} />
                    </div>
                </div>

                <h3 className={styles.sectionTitle}>Line Items</h3>
                <div className={styles.lineItemsContainer}>
                    <div className={styles.lineItemsHeader}>
                        <div className={styles.descriptionHeader}>Description</div>
                        <div>Quantity</div>
                        <div>Unit Price</div>
                        <div>Total</div>
                        <div>Action</div>
                    </div>
                    {lineItems.map((item, index) => (
                        <div key={index} className={styles.lineItem}>
                            <input type="text" placeholder="Service or Product Description" value={item.description} onChange={(e) => handleLineItemChange(index, 'description', e.target.value)} className={styles.descriptionInput} required disabled={isProcessing} />
                            <input type="number" min="0" step="any" value={item.quantity} onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)} required disabled={isProcessing} />
                            <input type="number" min="0" step="any" placeholder="0.00" value={item.unitPrice} onChange={(e) => handleLineItemChange(index, 'unitPrice', e.target.value)} required disabled={isProcessing} />
                            <div className={styles.lineTotal}>
                                {currencyILS((item.quantity || 0) * (item.unitPrice || 0))}
                            </div>
                            <button type="button" onClick={() => removeLineItem(index)} className={styles.removeButton} disabled={isProcessing || lineItems.length <= 1}>
                                <FiTrash2 />
                            </button>
                        </div>
                    ))}
                    <button type="button" onClick={addLineItem} className={styles.addButton} disabled={isProcessing}>
                        <FiPlus /> Add Line Item
                    </button>
                </div>

                <div className={styles.bottomSection}>
                    <div className={styles.notesSection}>
                        <label htmlFor="notes">Notes / Payment Instructions</label>
                        <textarea id="notes" rows="4" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g., Thank you for your business. Please pay within 30 days." disabled={isProcessing}></textarea>
                    </div>
                    <div className={styles.summarySection}>
                        <div className={styles.summaryRow}>
                            <span>Subtotal</span>
                            <span>{currencyILS(totals?.subtotal || 0)}</span>
                        </div>
                        <div className={styles.summaryRow}>
                            <label htmlFor="taxRate">Tax (%)</label>
                            <input type="number" id="taxRate" min="0" step="any" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} placeholder="e.g., 17" disabled={isProcessing} />
                        </div>
                        <div className={styles.summaryRow}>
                            <span>Tax Amount</span>
                            <span>{currencyILS(totals?.taxAmount || 0)}</span>
                        </div>
                        <div className={`${styles.summaryRow} ${styles.totalRow}`}>
                            <strong>Total Amount</strong>
                            <strong>{currencyILS(totals?.totalAmount || 0)}</strong>
                        </div>
                    </div>
                </div>

                <div className={styles.formFooter}>
                    {error && <p className={styles.errorMessage}>{error}</p>}
                    {successMessage && <p className={styles.successMessage}>{successMessage}</p>}
                    <button type="submit" className={styles.submitButton} disabled={isProcessing}>
                        {isProcessing ? 'Processing...' : 'Save & Send for Approval'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateInvoicePage;
